import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChildren,
  inject,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Timeline, TimelineItem } from 'www-cms';
import { DirectusDirective } from 'directus';
import { BuildingBlockOutletComponent } from '../../shared/components/building-block-outlet/building-block-outlet.component';
import { Subject, debounceTime, fromEvent, takeUntil } from 'rxjs';
import { RouterLink } from '@angular/router';
import { Store } from '@ngxs/store';
import { UserPrefState } from '../../shell/shell.state';

@Component({
  selector: 'ss-www-timeline',
  imports: [
    CommonModule,
    BuildingBlockOutletComponent,
    RouterLink,
    DirectusDirective,
  ],
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimelineComponent implements OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private elementRef = inject(ElementRef);

  @Input({ required: true }) timelineData!: Timeline;
  @Input({ required: true }) lastBlock!: boolean;
  @ViewChildren('sectionHeaders') sectionHeaders:
    | ElementRef<HTMLDivElement>[]
    | undefined;
  currentSection = signal<number>(0);
  hideMenu = signal<boolean>(false);
  destroy$ = new Subject<void>();

  isPromoBannerVisible$ = inject(Store).select(
    UserPrefState.isPromoBannerVisible
  );

  ngOnInit(): void {
    this.listenToScroll();
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Used to listen to the scroll event and set the current section
   * so the styling can be updated
   */
  listenToScroll(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    fromEvent(window, 'scroll')
      // Throttle the scroll event to only fire every 250ms
      .pipe(debounceTime(40), takeUntil(this.destroy$))
      .subscribe(() => {
        // Get the offset of each section header
        const sectionHeaders = this.sectionHeaders?.map((header) => {
          return header.nativeElement.offsetTop;
        });

        if (!sectionHeaders) {
          return;
        }

        const elementYLength = this.elementRef.nativeElement.offsetHeight + 300;

        if (window.scrollY > elementYLength) {
          this.hideMenu.set(true);
        } else {
          this.hideMenu.set(false);
        }
        // Find the index of the current section
        const currentSection = sectionHeaders.findIndex(
          (header) => header > window.scrollY + 300
        );
        // Set the index
        if (
          currentSection !== undefined &&
          currentSection !== this.currentSection()
        ) {
          if (currentSection === -1) {
            this.currentSection.set(sectionHeaders.length - 1);
          } else if (currentSection === 0) {
            this.currentSection.set(currentSection);
          } else {
            this.currentSection.set(currentSection - 1);
          }
        }
      });
  }

  setCurrentSection(index: number): void {
    this.currentSection.set(index);
  }

  trackTimelineItem(index: number, item: TimelineItem) {
    return item.id;
  }
}
